import { useUser } from "../contexts/UserContext";
import { motion } from "framer-motion";
import { Loader2, LogOut, Mail, Building2, Globe2, ShieldCheck } from "lucide-react";
import LinkedInCard from "../components/LinkedInCard";

const LEVEL_LABELS = {
  ceo: "CEO",
  direction: "Direction",
  dn: "Directeur National",
  dr: "Directeur Régional",
  consultant: "Consultant",
  president: "Président",
};

const LEVEL_DESC = {
  ceo: "Direction générale APOLLO",
  direction: "Comité de Direction",
  dn: "Direction nationale",
  dr: "Direction régionale",
  consultant: "Consultant indépendant",
  president: "Président de groupe",
};

export default function Me() {
  const { user, tenant, logout } = useUser();

  if (!user) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="animate-spin" color="#F77F00" />
      </div>
    );
  }

  const initials = `${user.first_name?.[0] || ""}${user.last_name?.[0] || ""}`.toUpperCase();

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      data-testid="me-page"
      className="max-w-2xl mx-auto"
    >
      <h1
        className="text-[28px] sm:text-[32px] font-bold leading-tight mb-2"
        style={{ fontFamily: "'Sora', sans-serif" }}
      >
        Mon profil
      </h1>
      <p className="text-sm mb-6" style={{ color: "#666" }}>
        Vos informations APOLLO et préférences.
      </p>

      <div
        className="bg-white rounded-[12px] p-6 sm:p-8 mb-4"
        style={{ border: "1px solid #E5E5E5" }}
      >
        <div className="flex items-center gap-5 mb-8">
          {user.avatar_url ? (
            <img
              src={user.avatar_url}
              alt=""
              className="w-20 h-20 rounded-full object-cover"
              style={{ border: "2px solid #FED7AA" }}
            />
          ) : (
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center text-2xl font-bold text-white"
              style={{ background: "#F77F00" }}
            >
              {initials || "?"}
            </div>
          )}
          <div className="min-w-0">
            <h2
              className="text-xl sm:text-2xl font-bold"
              style={{ color: "#1A1A1A", fontFamily: "'Sora', sans-serif" }}
              data-testid="me-name"
            >
              {user.first_name} {user.last_name}
            </h2>
            <div className="text-sm flex items-center gap-1.5 mt-1" style={{ color: "#666" }}>
              <Mail size={12} />
              <span data-testid="me-email">{user.email}</span>
            </div>
            <div
              className="inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-full text-xs font-semibold"
              style={{ background: "#FFF7ED", color: "#9A3412" }}
              data-testid="me-level-badge"
            >
              <ShieldCheck size={12} />
              {LEVEL_LABELS[user.hierarchy_level] || user.hierarchy_level}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field
            icon={ShieldCheck}
            label="Niveau hiérarchique"
            value={LEVEL_LABELS[user.hierarchy_level]}
            help={LEVEL_DESC[user.hierarchy_level]}
            testid="me-level"
          />
          <Field
            icon={Building2}
            label="Collège Direction"
            value={user.is_direction_committee ? "Membre" : "Non membre"}
            help={
              user.is_direction_committee
                ? "Peut modérer le pool Direction"
                : undefined
            }
            testid="me-direction-committee"
          />
          <Field
            icon={Globe2}
            label="Langue préférée"
            value={(user.language_preference || "fr").toUpperCase()}
            help={
              user.preferred_translation_language
                ? `Traduit en ${user.preferred_translation_language.toUpperCase()}`
                : undefined
            }
            testid="me-lang"
          />
          <Field
            icon={Building2}
            label="Tenant"
            value={tenant?.name || user.tenant_id?.slice(0, 8) + "…"}
            help={tenant?.product_name}
            testid="me-tenant"
          />
        </div>
      </div>

      {/* Connexion LinkedIn — brique optionnelle, se masque d'elle-même
          si l'endpoint de statut n'est pas disponible. */}
      <LinkedInCard />

      <button
        onClick={logout}
        data-testid="me-logout-button"
        className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-full text-sm font-semibold border transition-colors hover:bg-red-50"
        style={{ borderColor: "#FECACA", color: "#EF4444" }}
      >
        <LogOut size={16} /> Déconnexion
      </button>
    </motion.div>
  );
}

function Field({ icon: Ic, label, value, help, testid }) {
  return (
    <div
      className="p-4 rounded-lg"
      style={{ background: "#FAFAFA", border: "1px solid #E5E5E5" }}
      data-testid={testid}
    >
      <div
        className="text-xs uppercase tracking-wide mb-1.5 flex items-center gap-1.5"
        style={{ color: "#666" }}
      >
        <Ic size={12} /> {label}
      </div>
      <div className="text-base font-semibold" style={{ color: "#1A1A1A" }}>
        {value}
      </div>
      {help && (
        <div className="text-xs mt-1" style={{ color: "#999" }}>
          {help}
        </div>
      )}
    </div>
  );
}
